import { createContext, Fragment, useContext, type ReactNode } from 'react';
import { parseMdTable } from '@/features/dossier/mdTable';
import { EctdVersionNote } from './EctdVersionNote';
import { useSectionGuide } from './SectionGuidesContext';

/**
 * Shared rendering primitives used by both the eSTAR (510(k)) and eCTD
 * (NDA/BLA) renderers. Mirrors the legacy `renderEstar*` / `renderEctd*`
 * helpers in `frontend/static/js/dossier-renderer.js`.
 *
 * Placeholder discipline: the agent emits `__PLACEHOLDER__:<hint>` for
 * fields the submitter must still fill in — those surface inline as italic
 * amber chips. Empty/null values fall back to the field's own `fallbackHint`.
 */

const PLACEHOLDER_MARKER_PREFIX = '__PLACEHOLDER__:';
// The bare token: the agent sometimes emits "__PLACEHOLDER__" with no ":hint".
const PLACEHOLDER_TOKEN = '__PLACEHOLDER__';

// Text that TALKS ABOUT the marker ("while any __PLACEHOLDER__ marker remains")
// is not a missing value — it reads as the word, not a mid-sentence chip.
const MARKER_AS_WORD_RE = /\[?__PLACEHOLDER__\]?(?=\s+markers?\b)/g;

// A cover-letter subject line ("Re: …" / "Subject: …") — emphasized in bold
// so it stands out as the subject of the letter.
const COVER_SUBJECT_RE = /^(?:Re|Subject)\s*:/i;

/**
 * Split an agent-supplied value into (content, placeholderHint). Returns
 * [content, null] when the value is real content, [null, hint] when the
 * value is empty OR carries the placeholder marker.
 */
function resolveValue(v: unknown): [string | null, string | null] {
  if (v == null || v === '') return [null, null];
  if (typeof v === 'string') {
    // A second marker means two to-dos in one field: it stays content so
    // withInlinePlaceholders gives each marker its own chip (as the PDF and Word do).
    if (v.startsWith(PLACEHOLDER_MARKER_PREFIX) && v.split(PLACEHOLDER_TOKEN).length === 2) {
      return [null, v.slice(PLACEHOLDER_MARKER_PREFIX.length).trim() || null];
    }
    // Bare "__PLACEHOLDER__" with no ":hint" — still a placeholder, no hint.
    if (v.trim() === PLACEHOLDER_TOKEN) {
      return [null, null];
    }
  }
  return [typeof v === 'string' ? v : String(v), null];
}

export function Placeholder({ hint }: { hint: string | null | undefined }) {
  return (
    <span className="italic text-state-warning-text bg-state-warning-bg-soft border border-state-warning-border rounded px-1.5 py-0.5 text-[12.5px]">
      [Submitter must complete{hint ? `: ${hint}` : ''}]
    </span>
  );
}

/**
 * Render a string, converting any EMBEDDED `__PLACEHOLDER__:hint` marker into a
 * colored Placeholder chip so an inline marker is as obvious on-screen as it is
 * in the PDF (which styles every occurrence). Without this, a marker sitting
 * mid-prose renders as plain black text like the rest of the report.
 *
 * Mirrors the backend `_inline_placeholders`: split on the marker — the leading
 * piece is real text, and each following piece is a hint that runs to the next
 * marker (or the end of the string).
 */
export function withInlinePlaceholders(text: string): ReactNode {
  if (!text.includes(PLACEHOLDER_TOKEN)) return text;
  // Split on the bare token. Each following piece is either ":hint…" (colon
  // form — the hint runs to the next token) or normal text after a bare marker.
  const [lead, ...rest] = text.split(PLACEHOLDER_TOKEN);
  return (
    <>
      {lead}
      {rest.map((part, i) =>
        part.startsWith(':') ? (
          <Placeholder key={i} hint={part.slice(1).trim() || null} />
        ) : (
          <Fragment key={i}>
            <Placeholder hint={null} />
            {part}
          </Fragment>
        ),
      )}
    </>
  );
}

/**
 * `withInlinePlaceholders` for any text in a drug & biologic (eCTD) dossier too: there, text that
 * talks about the marker reads as the word, as the eCTD PDF prints it everywhere (not only in
 * prose paragraphs). The device pages' PDFs have no such rule, so they render as before.
 */
function InlineText({ text }: { text: string }) {
  const ctd = useContext(CtdDossierContext);
  return <>{withInlinePlaceholders(ctd ? text.replace(MARKER_AS_WORD_RE, 'placeholder') : text)}</>;
}

/**
 * Label + value in the value column. Empty/marker values render the placeholder. A value with
 * line breaks (an address) keeps them, in the value column, never under the label.
 */
export function Field({
  label,
  value,
  fallbackHint,
}: {
  label: string;
  value: unknown;
  fallbackHint?: string;
}) {
  const [content, embeddedHint] = resolveValue(value);
  return (
    <div className="grid grid-cols-[minmax(180px,220px)_1fr] gap-x-4 gap-y-0.5 my-2">
      <div className="font-semibold text-ink/80 text-[13px]">{label}</div>
      <div className="text-[13.5px] min-w-0 whitespace-pre-line">
        {content !== null ? (
          <InlineText text={content} />
        ) : (
          <Placeholder hint={embeddedHint || fallbackHint || label.replace(/:$/, '')} />
        )}
      </div>
    </div>
  );
}

/**
 * Multi-paragraph prose field. Splits the body on blank lines.
 */
export function FieldProse({
  label,
  body,
  fallbackHint,
}: {
  /** Omit (or pass '') to render the prose WITHOUT a label — e.g. §21 items
   *  where the numbered/sub heading already titles the content. */
  label?: string;
  body: unknown;
  fallbackHint?: string;
}) {
  const [content, embeddedHint] = resolveValue(body);
  return (
    <div className="my-3">
      {label ? <div className="font-semibold text-ink/80 text-[13px] mb-1">{label}</div> : null}
      <div className="text-[13.5px] leading-[1.6]">
        {content !== null ? (
          <ProseParagraphs body={content} />
        ) : (
          <Placeholder hint={embeddedHint || fallbackHint || (label ?? '').replace(/:$/, '')} />
        )}
      </div>
    </div>
  );
}

/**
 * True inside a drug & biologic (eCTD) dossier — `EctdRenderer` provides it. Matches
 * the eCTD PDF: a paragraph keeps its single line breaks (a letter's address block),
 * an AI-written markdown pipe table renders as a table, and a yes/no section never
 * says "subject device" (no device crossover). Device dossier views never set it, so
 * they render exactly as before.
 */
export const CtdDossierContext = createContext(false);

/**
 * True inside a U.S. drug & biologic (eCTD) dossier only: `EctdRenderer` provides it, the EU
 * page (`EuCtdRenderer`) does not. A `ProseSubsection` that is only the submitter's box then
 * still lists the citations the run gave it, under the box (Task DS, D-a). Display only.
 * Lockstep with `citations_under_placeholder` in `backend/services/dossier/ectd_renderer/`.
 */
export const CitationsUnderPlaceholderContext = createContext(false);

function RichParagraphs({ body }: { body: string }) {
  const nodes: ReactNode[] = [];
  const pushText = (lines: string[]) => {
    const kept = lines.map((line) => line.trim()).filter(Boolean);
    const first = kept[0];
    if (first === undefined) return;
    nodes.push(
      <p
        key={nodes.length}
        className={`m-0 my-1.5${COVER_SUBJECT_RE.test(first) ? ' font-bold text-ink' : ''}`}
      >
        {kept.map((line, i) => (
          <Fragment key={i}>
            {i > 0 ? <br /> : null}
            {withInlinePlaceholders(line)}
          </Fragment>
        ))}
      </p>,
    );
  };
  for (const block of body.replace(MARKER_AS_WORD_RE, 'placeholder').split(/\n\s*\n/)) {
    const lines = block.trim().split('\n');
    const isRow = (k: number) => (lines[k] ?? '').trimStart().startsWith('|');
    let text: string[] = [];
    let i = 0;
    while (i < lines.length) {
      if (isRow(i)) {
        let j = i;
        while (j < lines.length && isRow(j)) j += 1;
        const table = parseMdTable(lines.slice(i, j));
        if (table) {
          pushText(text);
          text = [];
          nodes.push(
            <DossierTable key={nodes.length} headers={table.headers} rows={table.rows} blankEmptyCells />,
          );
          i = j;
          continue;
        }
      }
      text.push(lines[i] ?? '');
      i += 1;
    }
    pushText(text);
  }
  return <>{nodes}</>;
}

function ProseParagraphs({ body }: { body: string }) {
  const ctd = useContext(CtdDossierContext);
  if (ctd) return <RichParagraphs body={body} />;
  const paragraphs = body
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (paragraphs.length === 0 && body.trim()) {
    return <p className="m-0 my-1.5">{withInlinePlaceholders(body.trim())}</p>;
  }
  return (
    <>
      {paragraphs.map((p, i) => (
        <p
          key={i}
          className={`m-0 my-1.5${COVER_SUBJECT_RE.test(p) ? ' font-bold text-ink' : ''}`}
        >
          {withInlinePlaceholders(p)}
        </p>
      ))}
    </>
  );
}

/** The labels of REGARA's boxes on every dossier page, every country and product type alike
 *  (founder 2026-11-02: "not part of submission", no authority named; no dash, so each reads
 *  as a plain sentence). Lockstep with backend/services/dossier/regara_labels.py. */
export const REGARA_NOTE_LABEL = 'REGARA note (not part of submission)';
export const REGARA_GUIDE_LABEL = 'REGARA guide (not part of submission)';
export const REGARA_NOTES_LABEL = 'REGARA notes (not part of submission)';
/** The caption of a section's guide in the REGARA box (lockstep with the PDF's "What belongs here"). */
const WHAT_BELONGS_CAPTION = 'What belongs here';

/**
 * Yes/No gating block — question + response with applicability suffix +
 * rationale (or rationale-for-non-applicability).
 */
export function Gating({
  question,
  section,
  alwaysAdvisory = false,
  authority = 'FDA',
  applicantStatement = false,
  guide = null,
}: {
  question: string;
  section: { gating_response?: unknown; rationale?: unknown } | undefined;
  /** When true, a "Yes" gate whose rationale is REGARA guidance / a
   *  submitter-to-provide placeholder also renders in the decision-support box
   *  (default false → 510(k)/De Novo behavior unchanged). */
  alwaysAdvisory?: boolean;
  /** The regulator named in the advisory heading ("before using in your <authority> submission"). */
  authority?: string;
  /** U.S. biologics: a "No" section's reason is the applicant's own statement, printed as plain FDA
   *  text with no REGARA box (lockstep with `_ectd_gating(applicant_statement=True)` in the PDF). A
   *  reason that is itself a placeholder keeps the box, so a gap stays a visible to-do. */
  applicantStatement?: boolean;
  /** A U.S. Modules 2 to 5 section's REGARA guide (Task 3). When this block shows the REGARA box,
   *  the guide goes first inside it, then the reason: one box per section (review fix 2, lockstep
   *  with `_ectd_gating(guide_html=)` in the PDF). Otherwise it shows in its own box above the block. */
  guide?: string | null;
}) {
  const ctd = useContext(CtdDossierContext);
  const subject = ctd ? '' : ' to subject device';
  const response = String(section?.gating_response || 'No');
  const rationale = section?.rationale ?? '';
  const norm = response.trim().toLowerCase();
  let responseSuffix = '';
  const rationaleLabel = 'Rationale:';
  if (norm === 'yes') {
    responseSuffix = ` — section is applicable${subject}.`;
  } else if (norm === 'no') {
    responseSuffix = ` — section is not applicable${subject}.`;
  }
  const showAdvisory = norm === 'no' || (norm === 'yes' && alwaysAdvisory);
  const advisoryHeading =
    norm === 'no'
      ? `Why this section is not applicable (review & re-author before using in your ${authority} submission):`
      : `REGARA guidance — what the sponsor must provide for this section (review & re-author before using in your ${authority} submission):`;
  const [content, hint] = resolveValue(rationale);
  // The CTD views print the rationale as the eCTD PDF does (`_ectd_gating` -> `_render_paragraphs`):
  // per paragraph and per line, so an inline hint ends at its line and the text after it stays
  // document text. The eSTAR PDF prints it as one string (a hint runs to the next marker), and the
  // device pages keep doing that.
  const rationaleBody =
    content === null ? null : ctd ? (
      <RichParagraphs body={content} />
    ) : (
      <p className="m-0"><InlineText text={content} /></p>
    );
  const printsApplicantStatement =
    applicantStatement && ctd && authority === 'FDA' && norm === 'no' && content !== null;
  const guideInBox = guide !== null && showAdvisory && !printsApplicantStatement;
  return (
    <>
      {guide !== null && !guideInBox && <WhatBelongsNote belongs={guide} />}
      <div className="my-3 border-l-2 border-line pl-4 py-1">
        <p className="m-0 mb-1 text-[13px] text-muted">
          <strong className="text-navy">Gating question:</strong> {question}
        </p>
        <p className="m-0 mb-2 text-[13.5px]">
          <strong>Response: {response}</strong>
          {responseSuffix}
        </p>
        {printsApplicantStatement ? (
          <div className="text-[13.5px] leading-[1.6]">{rationaleBody}</div>
        ) : showAdvisory ? (
          // RA feedback: a "not applicable" rationale (or, for an applicable section, a
          // REGARA "sponsor must provide X" note) reads like fileable eSTAR justification,
          // so mark it clearly as a REGARA guide (advisory box + italic body, every country's
          // REGARA_GUIDE_LABEL) so it is not mistaken for submission prose.
          <RegaraNoteFrame label={REGARA_GUIDE_LABEL}>
            {guideInBox && (
              <>
                <div className="font-semibold text-ink/80 text-[12.5px] mb-1">{WHAT_BELONGS_CAPTION}</div>
                <div className="italic text-ink/80 leading-[1.6] mb-2">
                  <p className="m-0">{guide}</p>
                </div>
              </>
            )}
            <div className="font-semibold text-ink/80 text-[12.5px] mb-1">
              {advisoryHeading}
            </div>
            <div className="italic text-ink/80 leading-[1.6]">
              {rationaleBody ?? <Placeholder hint={hint || 'Gating rationale'} />}
            </div>
          </RegaraNoteFrame>
        ) : (
          <div className="text-[13.5px]">
            <div className="font-semibold text-ink/80 text-[12.5px] mb-1">{rationaleLabel}</div>
            <div className="leading-[1.6]">
              {rationaleBody ?? <Placeholder hint={hint || 'Gating rationale'} />}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Tabular block. Headers + rows of stringly-typed cells. Empty/marker cells
 * render the per-cell placeholder; an empty rows list renders a single
 * placeholder row spanning all columns.
 */
/** A note's text when it has something to say; null for a missing, blank or to-do value. */
export function regaraNoteText(body: unknown): string | null {
  const text = typeof body === 'string' ? body : '';
  return !text.trim() || text.trim().startsWith('__PLACEHOLDER__') ? null : text;
}

/**
 * A clearly-marked REGARA note box (`REGARA_NOTE_LABEL`, every country's) for
 * decision-support / traceability remarks that must not be mistaken for fileable
 * content (lockstep with backend `_estar_regara_note`). Renders nothing for an
 * empty / placeholder body.
 */
export function RegaraNote({
  body,
  caption,
}: {
  body: unknown;
  caption?: string;
  /** Unused: the label names no authority now. Accepted until the EU CTD view (branch
   *  eu-medicinal-pathway-prompts) stops passing it; then delete. */
  authority?: string;
}) {
  const text = regaraNoteText(body);
  if (text === null) return null;
  return (
    <RegaraNoteFrame label={REGARA_NOTE_LABEL} caption={caption}>
      <div className="italic text-ink/80 leading-[1.6]"><InlineText text={text} /></div>
    </RegaraNoteFrame>
  );
}

/** The one REGARA advisory box (state-warning palette) both note shapes render in. */
/**
 * The one REGARA box: its label (and caption) as a header the reader clicks to fold the box, open
 * by default (founder 2026-11-02: every REGARA box folds, on the page as in the Word file).
 */
function RegaraNoteFrame({
  label,
  caption,
  children,
}: {
  label: string;
  caption?: ReactNode;
  children: ReactNode;
}) {
  return (
    <details open className="group my-2 rounded border border-state-warning-border bg-state-warning-bg-soft px-3 py-2">
      {/* The fold arrow is drawn by CSS, so the box's text (what a reader copies, what tests read)
          stays its label, caption and body; the label and caption stay selectable (code review
          2026-11-03), so a reviewer can copy a check's name. */}
      <summary className="flex cursor-pointer items-start gap-1.5 marker:content-[''] [&::-webkit-details-marker]:hidden">
        <span
          aria-hidden="true"
          className="text-[10px] leading-[15px] text-state-warning-text transition-transform group-open:rotate-90 before:content-['▸']"
        />
        <span>
          <span className="block text-[10px] font-bold uppercase tracking-[0.04em] text-state-warning-text">
            {label}
          </span>
          {caption && <span className="mt-1 block font-semibold text-ink/80 text-[12.5px]">{caption}</span>}
        </span>
      </summary>
      <div className="mt-1">{children}</div>
    </details>
  );
}

/**
 * A U.S. Module 1 section's "What belongs here" guidance (Task UI1), in the one REGARA box
 * under the same label as a gating rationale. `children` is the submitter's placeholder when
 * the section is the submitter's box, so it sits inside this same box (one box per section);
 * what a run wrote stays outside. Lockstep with `_what_belongs_box` in
 * `backend/services/dossier/ectd_renderer/usa.py`.
 */
export function WhatBelongsNote({ belongs, children }: { belongs: string; children?: ReactNode }) {
  return (
    <RegaraNoteFrame label={REGARA_GUIDE_LABEL} caption={WHAT_BELONGS_CAPTION}>
      <div className="italic text-ink/80 leading-[1.6]">
        <p className="m-0">{belongs}</p>
        {children ? <div className="mt-1">{children}</div> : null}
      </div>
    </RegaraNoteFrame>
  );
}

/**
 * Regara's fixed advisories on an EU / NI device part (Declaration of Conformity, UDI /
 * EUDAMED data, conformity route, clinical requirements, GSPR checklist, PSUR, performance
 * evaluation) — the same REGARA box as the EU drug page, so they are never read as text for
 * the Notified Body. Lockstep with the PDF's ``regara_notes_html``. Nothing when empty.
 */
export function RegaraNoteList({ notes }: { notes: readonly unknown[] }) {
  const items = notes.filter((n): n is string => typeof n === 'string' && n.trim() !== '');
  if (items.length === 0) return null;
  return (
    <RegaraNoteFrame label={REGARA_NOTES_LABEL}>
      <ul className="list-disc pl-5 m-0 space-y-1 italic text-ink/80 leading-[1.6]">
        {items.map((n, i) => (
          <li key={i}><InlineText text={n} /></li>
        ))}
      </ul>
    </RegaraNoteFrame>
  );
}

/**
 * The REGARA check notes on an EU / NI device document — ``dossier.regara_checks`` =
 * ``[{check, note}]`` from app/dossier/device_checks.py (standards, citations) — each in the
 * REGARA note box, as on the EU drug page. Rendered at the top of every device document
 * (MDR TD / PMS / SSCP, IVDR TD / PMS / SSP), single-run and complete-dossier page alike;
 * lockstep with the PDF's ``regara_checks_html``. Nothing when the run has no notes.
 */
export function DeviceCheckNotes({ dossier }: { dossier: Record<string, unknown> }) {
  const checks = dossier['regara_checks'];
  if (!Array.isArray(checks)) return null;
  return (
    <>
      {checks.map((c, i) => {
        const row = (c ?? {}) as { check?: unknown; note?: unknown };
        return (
          <RegaraNote
            key={i}
            body={row.note}
            caption={typeof row.check === 'string' ? row.check : undefined}
          />
        );
      })}
    </>
  );
}

/**
 * A marked REGARA guide box (`REGARA_GUIDE_LABEL`) around an
 * analytical prose field (e.g. the §9 predicate-selection rationale) so REGARA's
 * reasoning is not mistaken for fileable FDA text. Renders multi-paragraph prose,
 * or the field placeholder when empty; ALWAYS renders (lockstep with backend
 * `_estar_regara_prose_note`).
 */
// The box already prints its REGARA guide label, but the agent (whose prompt still names the old
// "REGARA decision-support — not FDA submission text" box) sometimes prepends that marker into the
// body prose,
// so it reads twice. Strip a leading self-referential marker (up to its first
// colon) at render time — mirrors _REGARA_MARKER_PREFIX in the backend shared.py.
const REGARA_MARKER_PREFIX =
  /^\s*REGARA\s+decision[-\s]?support\b[^:\n]*?not\s+FDA\s+submission\s+text[^:\n]*?:\s*/i;

export function RegaraProseNote({
  label,
  body,
  fallbackHint,
}: {
  label: string;
  body: unknown;
  fallbackHint?: string;
}) {
  const [rawContent, embeddedHint] = resolveValue(body);
  let content = rawContent;
  if (typeof rawContent === 'string') {
    const stripped = rawContent.replace(REGARA_MARKER_PREFIX, '');
    if (stripped.trim()) content = stripped;
  }
  return (
    <RegaraNoteFrame
      label={REGARA_GUIDE_LABEL}
      caption={
        <>
          {label}{' '}
          <span className="font-normal italic text-state-warning-text">
            (review &amp; re-author before using in your FDA submission)
          </span>
        </>
      }
    >
      <div className="italic text-ink/80 leading-[1.6]">
        {content !== null ? (
          <ProseParagraphs body={content} />
        ) : (
          <Placeholder hint={embeddedHint || fallbackHint || label.replace(/:$/, '')} />
        )}
      </div>
    </RegaraNoteFrame>
  );
}

export function DossierTable({
  headers,
  rows,
  placeholderHint = 'Table rows to be supplied by submitter',
  blankEmptyCells = false,
}: {
  headers: string[];
  rows: unknown[][] | undefined;
  placeholderHint?: string;
  /** An empty cell stays blank instead of showing the submitter's to-do: a table the model wrote
   *  inside a paragraph, where a blank cell is data (the eCTD PDF prints it blank). The tables
   *  built from the dossier's own rows keep the to-do. */
  blankEmptyCells?: boolean;
}) {
  return (
    <div className="overflow-x-auto my-2">
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr>
            {headers.map((h) => (
              <th
                key={h}
                className="text-left p-2 bg-[#f3f4f6] text-muted uppercase tracking-[0.04em] text-[11px] border-b border-line"
              >
                {withInlinePlaceholders(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!rows || rows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} className="p-2 border-b border-line">
                <Placeholder hint={placeholderHint} />
              </td>
            </tr>
          ) : (
            rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => {
                  const [content, hint] = resolveValue(cell);
                  const blank = blankEmptyCells && cell === '';
                  return (
                    <td key={ci} className="p-2 border-b border-line align-top">
                      {content !== null ? (
                        <InlineText text={content} />
                      ) : blank ? null : (
                        <Placeholder hint={hint} />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Bulleted checklist of confirmed-present items (labeling components,
 * required-content checks, etc.).
 */
export function Checklist({ items }: { items: unknown[] | undefined }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="list-disc pl-5 m-0 space-y-1 text-[13.5px]">
      {items.map((it, i) => (
        <li key={i}><InlineText text={String(it)} /></li>
      ))}
    </ul>
  );
}

/**
 * Field group with a label + an arbitrary body (used for "X table:" / "Y
 * checklist:" rows). Falls back to a placeholder body if no children pass.
 */
export function FieldGroup({
  label,
  fallbackHint,
  children,
}: {
  label: string;
  fallbackHint?: string;
  children: ReactNode;
}) {
  return (
    <div className="my-3">
      <div className="font-semibold text-ink/80 text-[13px] mb-1">{label}</div>
      <div className="text-[13.5px]">
        {children ?? <Placeholder hint={fallbackHint || label.replace(/:$/, '')} />}
      </div>
    </div>
  );
}

/**
 * True inside a container that is already the card (the complete EU device dossier draws ONE
 * card per Part, as the CTD view draws one per Module) — sections then render as headed blocks
 * inside it instead of a card each.
 */
export const InPartCardContext = createContext(false);

/**
 * A panel's own card (cover / identity block, state messages). Inside a Part card it renders as
 * a plain block, so the Part stays ONE card instead of cards inside a card.
 */
export function PanelCard({ children }: { children: ReactNode }) {
  const inPartCard = useContext(InPartCardContext);
  return (
    <div className={inPartCard ? 'pb-2' : 'bg-white border border-line rounded-2xl p-6 shadow-card'}>
      {children}
    </div>
  );
}

/**
 * Numbered section wrapper used by both eSTAR (Section N — Title) and eCTD
 * (Module N — Title). The `prefix` lets each renderer pass its own label.
 */
export function NumberedSection({
  prefix,
  num,
  title,
  children,
}: {
  prefix: 'Section' | 'Module';
  num: number;
  title: string;
  children: ReactNode;
}) {
  return <TitledSection title={`${prefix} ${num} — ${title}`}>{children}</TitledSection>;
}

/**
 * A titled section: its own card, or — inside a Part card — a headed block within it.
 * NumberedSection is this with a "Section N —" / "Module N —" title.
 */
export function TitledSection({ title, children }: { title: string; children: ReactNode }) {
  const inPartCard = useContext(InPartCardContext);
  return (
    <section
      className={
        inPartCard
          ? 'text-[14px] text-ink leading-[1.55] pt-6 first:pt-0'
          : 'bg-white border border-[#e0e0e0] rounded-xl p-[22px] text-[14px] text-ink leading-[1.55]'
      }
    >
      <h3 className="text-[17px] font-bold text-navy mt-0 mb-3 pb-1 border-b border-line">{title}</h3>
      <div>{children}</div>
    </section>
  );
}

/**
 * eCTD-only sub-section heading (e.g., "1.3.1 Applicant Information"). Under it, the eCTD
 * v3.2.2 / v4.0 note when `EctdRenderer` provides one for this heading (the U.S. medicinal
 * types), then a Modules 2 to 5 section's REGARA guide in the "What belongs here" box when
 * `EctdRenderer` provides one (`SectionGuidesContext`, Task 3); the section's own text and its
 * submitter's box stay below, outside the box. Lockstep with `_section_guides_html` in the PDF.
 */
export function SubsectionHeading({
  number,
  title,
  showGuide = true,
}: {
  number: string;
  title: string;
  /** False for a yes/no section: its `Gating` places the guide (see `GatingSubsection`). */
  showGuide?: boolean;
}) {
  const guide = useSectionGuide(number);
  return (
    <>
      <h4 className="text-[14px] font-bold text-navy mt-4 mb-1.5 pb-0.5 border-b border-line">
        {number} {title}
      </h4>
      <EctdVersionNote number={number} />
      {showGuide && guide && <WhatBelongsNote belongs={guide} />}
    </>
  );
}

/**
 * eCTD prose subsection: heading + paragraphs + optional citations block. `whatBelongs` (the
 * U.S. medicinal Module 1 sections only, Task M1; never passed by the EU CTD view) adds REGARA's
 * "What belongs here" guidance in the REGARA box under the heading (`WhatBelongsNote`, Task
 * UI1): a section that is only the submitter's box shows its placeholder inside that same box,
 * and what the run wrote stays outside it. Without it the output is unchanged. Inside
 * `CitationsUnderPlaceholderContext` (the U.S. eCTD page) a section that is only the
 * submitter's box also lists its citations under the box.
 */
export function ProseSubsection({
  number,
  title,
  prose,
  placeholderHint,
  whatBelongs,
}: {
  number: string;
  title: string;
  prose: { body?: unknown; citations?: unknown[] } | null | undefined;
  placeholderHint?: string;
  whatBelongs?: string;
}) {
  const body = prose && typeof prose === 'object' ? prose.body : null;
  const citations =
    prose && typeof prose === 'object' && Array.isArray(prose.citations)
      ? (prose.citations as unknown[])
      : [];
  const [content, embeddedHint] = resolveValue(body);
  const citationsUnderPlaceholder = useContext(CitationsUnderPlaceholderContext);
  const citationList = citations.length > 0 && (
    <div className="mt-2">
      <p className="m-0 mb-1">
        <em>Citations:</em>
      </p>
      <ul className="list-disc pl-5 m-0 space-y-0.5 text-[12.5px]">
        {citations.filter(Boolean).map((c, i) => (
          <li key={i}>{String(c)}</li>
        ))}
      </ul>
    </div>
  );
  const placeholder =
    content === null ? <Placeholder hint={embeddedHint || placeholderHint || title} /> : null;
  return (
    <div className="my-3">
      <SubsectionHeading number={number} title={title} />
      {whatBelongs && <WhatBelongsNote belongs={whatBelongs}>{placeholder}</WhatBelongsNote>}
      <div className="text-[13.5px] leading-[1.6]">
        {content !== null ? (
          <>
            <ProseParagraphs body={content} />
            {citationList}
          </>
        ) : (
          <>
            {!whatBelongs && placeholder}
            {citationsUnderPlaceholder && citationList}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * eCTD gating subsection: heading + gating block.
 */
export function GatingSubsection({
  number,
  title,
  gating,
  question,
  authority,
  applicantStatement,
}: {
  number: string;
  title: string;
  gating: { gating_response?: unknown; rationale?: unknown } | undefined;
  question: string;
  /** Passed through to `Gating` — the regulator named in its advisory box. */
  authority?: string;
  /** Passed through to `Gating` — a U.S. biologic's "No" reason prints as the applicant's statement. */
  applicantStatement?: boolean;
}) {
  const guide = useSectionGuide(number);
  return (
    <div className="my-3">
      <SubsectionHeading number={number} title={title} showGuide={false} />
      <Gating
        question={question}
        section={gating}
        authority={authority}
        applicantStatement={applicantStatement}
        guide={guide}
      />
    </div>
  );
}

/**
 * Convenience: pluck a sub-object from a parent dict, returning {} when the
 * key is missing or not an object so destructuring + property reads never
 * throw.
 */
export function obj(parent: Record<string, unknown> | null | undefined, key: string): Record<string, unknown> {
  const v = parent?.[key];
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/**
 * Convenience: read a prose-object's body field (used everywhere FieldProse
 * is fed a `prose` shape like { body: "...", citations: [...] }).
 */
export function prose(p: unknown): unknown {
  if (p && typeof p === 'object' && !Array.isArray(p)) {
    return (p as { body?: unknown }).body ?? null;
  }
  return null;
}

// Normalize a standalone "Not applicable" answer value to canonical
// "Not Applicable" (lockstep with backend shared._canon_na) — keeps the
// reprocessing/sterility/shelf-life discrete answer fields consistent with the
// RA-approved gating casing. Only the exact standalone phrase is touched; "N/A",
// "Yes", "No", real methods, and prose sentences stay. SHARED by the 510(k)
// (EstarRenderer) and PMA (PmaRenderer) results-page renderers.
export function canonNA(v: unknown): unknown {
  return typeof v === 'string' &&
    v.trim().replace(/\.+$/, '').trim().toLowerCase() === 'not applicable'
    ? 'Not Applicable'
    : v;
}

/**
 * §6 SaMD-aware device-description fields: device overview + principle of
 * operation (FDA-required for every device) and the software depth
 * (functionality/outputs, general specification, software & algorithm summary).
 * ALL render omit-when-empty so the change is purely ADDITIVE — legacy dossiers,
 * and hardware devices (which leave the software fields at placeholder), render
 * unchanged. Shared by the non-IVD §6 (EstarRenderer) and the IVD §6
 * (IvdSections). Lockstep with backend shared._estar_devdesc_510k_body.
 */
// True when a prose body is real content (not empty / not a __PLACEHOLDER__).
// Exported so device-type-adaptive §6 renderers can omit-when-empty (e.g. the
// hardware `detailed_description` on a SaMD). Lockstep with backend _resolve_value.
export function hasReal(body: unknown): boolean {
  const t = typeof body === 'string' ? body : '';
  return t.trim().length > 0 && !t.trim().startsWith('__PLACEHOLDER__');
}

// §16 Software/Firmware — FDA §VI Recommended Documentation. SHARED by the 510(k)
// (§16), De Novo (§19) and PMA (§16) renderers (lockstep with backend
// shared._estar_s16_software_doc_body). No software → gating block; software → the
// §VI framework (Documentation Level + rationale + description + §VI.C-J artifacts).
export function Section16SoftwareDoc({
  dossier,
  sectionKey,
  num,
}: {
  dossier: Record<string, unknown>;
  sectionKey: string;
  num: number;
}) {
  const s = obj(dossier, sectionKey);
  const isYes = String(s.gating_response ?? 'No').trim().toLowerCase() === 'yes';
  if (!isYes) {
    return (
      <NumberedSection prefix="Section" num={num} title="Software / Firmware">
        <Gating
          question="Does the subject device contain software or firmware, or is it intended to be used in combination with software?"
          section={s}
        />
      </NumberedSection>
    );
  }
  const checklist: [string, unknown][] = [
    ['Risk Management File (§VI.C)', s.risk_management_file],
    ['Software Requirements Specification / SRS (§VI.D)', s.software_requirements_specification],
    ['System & Software Architecture Design (§VI.E)', s.architecture_design],
    ['Software Design Specification / SDS (§VI.F)', s.software_design_specification],
    ['Development, Configuration Mgmt & Maintenance (§VI.G)', s.development_configuration_maintenance],
    ['Verification & Validation Testing (§VI.H)', s.verification_validation_testing],
    ['Software Version History (§VI.I)', s.version_history],
    ['Unresolved Software Anomalies (§VI.J)', s.unresolved_anomalies],
  ];
  return (
    <NumberedSection prefix="Section" num={num} title="Software / Firmware">
      <Field label="Software present:" value="Yes — the device includes a device software function" />
      <Field label="Documentation Level (per FDA §V):" value={s.documentation_level} />
      <RegaraProseNote label="Documentation Level rationale:" body={prose(s.documentation_level_rationale)} />
      <FieldProse label="Software Description (§VI.B):" body={prose(s.software_description)} />
      <SubsectionHeading
        number=""
        title="Recommended documentation (FDA guidance §VI) — submitter engineering artifacts"
      />
      <DossierTable
        headers={['Documentation element (FDA §VI)', 'Submission status / reference']}
        rows={checklist.map(([name, status]) => [name, status])}
      />
    </NumberedSection>
  );
}

export function Section17CyberInteropDoc({
  dossier,
  sectionKey,
  num,
}: {
  dossier: Record<string, unknown>;
  sectionKey: string;
  num: number;
}) {
  const s = obj(dossier, sectionKey);
  const isYes = String(s.gating_response ?? 'No').trim().toLowerCase() === 'yes';
  if (!isYes) {
    return (
      <NumberedSection prefix="Section" num={num} title="Cybersecurity / Interoperability">
        <Gating
          question="Does the subject device include any cyber capability (network, wired or wireless connectivity, removable media, etc.) or is it intended to exchange information with other devices or systems?"
          section={s}
        />
      </NumberedSection>
    );
  }
  // FDA cybersecurity Appendix 4 + interoperability §VI — two sub-block checklists.
  const cyber: [string, unknown][] = [
    ['Threat Model', s.threat_model],
    ['Cybersecurity Risk Assessment', s.security_risk_assessment],
    ['Software Bill of Materials (SBOM)', s.sbom],
    ['Vulnerability Assessment & Software Support', s.vulnerability_assessment],
    ['Unresolved Security Anomalies', s.unresolved_security_anomalies],
    ['Security Requirements', s.security_requirements],
    ['Security Architecture Views', s.security_architecture_views],
    ['Cybersecurity Testing', s.security_testing],
    ['Security Traceability', s.security_traceability],
    ['Security Measures & Metrics', s.security_measures_metrics],
    ['Cybersecurity Risk Management Report', s.security_risk_management_report],
    ['Cybersecurity Management Plan', s.cybersecurity_management_plan],
    ['Cybersecurity Labeling', s.cybersecurity_labeling],
  ];
  const interop: [string, unknown][] = [
    ['Interoperability Risk Analysis', s.interoperability_risk_analysis],
    ['Interoperability Verification & Validation', s.interoperability_vv],
    ['Interoperability Labeling', s.interoperability_labeling],
  ];
  return (
    <NumberedSection prefix="Section" num={num} title="Cybersecurity / Interoperability">
      <RegaraProseNote label="Applicability rationale:" body={prose(s.applicability_rationale)} />
      <FieldProse
        label="Connectivity / interface description (interop §VI.A):"
        body={prose(s.connectivity_description)}
      />
      <SubsectionHeading
        number=""
        title="Cybersecurity documentation (FDA guidance Appendix 4) — submitter engineering artifacts"
      />
      <DossierTable
        headers={['Documentation element (cybersecurity)', 'Submission status / reference']}
        rows={cyber.map(([n, v]) => [n, v])}
      />
      <SubsectionHeading
        number=""
        title="Interoperability documentation (FDA guidance §VI) — submitter engineering artifacts"
      />
      <DossierTable
        headers={['Documentation element (interoperability)', 'Submission status / reference']}
        rows={interop.map(([n, v]) => [n, v])}
      />
    </NumberedSection>
  );
}

export function Section6SamdFields({ s }: { s: Record<string, unknown> }) {
  const field = (label: string, key: string) =>
    hasReal(prose(s[key])) ? <FieldProse label={label} body={prose(s[key])} /> : null;
  return (
    <>
      {field('Device overview:', 'device_overview')}
      {field('Principle of operation:', 'principle_of_operation')}
      {field('Functionality and outputs:', 'functionality_and_outputs')}
      {field('General specification:', 'general_specification')}
      {field('Software and algorithm summary:', 'software_algorithm_summary')}
    </>
  );
}
